import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { urlValidator } from '../../functions/url-validator';
import { Button } from "../tailwind/button";
import {
  ErrorMessage,
  Field,
  Fieldset,
  Legend
} from "../tailwind/fieldset";
import { Heading } from "../tailwind/heading";
import { Input, InputGroup } from "../tailwind/input";
import { Text } from "../tailwind/text";
import { Textarea } from "../tailwind/textarea";

export const AIChatbot = ({
  selectedNode,
  user,
  handleMessageChange,
  nodes,
  setNodes,
  setGaiaWebsiteDatasources,
}) => {
  const { gaiaWebsiteDatasources, assistantContext, companyContext, answerStyle } = selectedNode.data;

  const propagateGaiaWebsiteDatasources = (value) => {
    handleMessageChange("gaiaWebsiteDatasources", value, nodes, setNodes);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === "ai") {
          node.data = {
            ...node.data,
            gaiaWebsiteDatasources: value,
          };
        }
        return node;
      })
    );

    setGaiaWebsiteDatasources(value)
  }

  const handleGaiaWebsiteDatasourceUrlChange = (url, index) => {
    const _gaiaWebsiteDatasources = [].concat(gaiaWebsiteDatasources);
    _gaiaWebsiteDatasources[index] = { url: (url || '').trim() || null, id: null };
    propagateGaiaWebsiteDatasources(_gaiaWebsiteDatasources);
  }

  const addGaiaWebsiteDatasourceUrl = () => {
    propagateGaiaWebsiteDatasources([].concat(gaiaWebsiteDatasources || []).concat([{ id: null, url: '' }]));
  }

  const deleteGaiaWebsiteDatasourceUrl = (index) => {
    const _gaiaWebsiteDatasources = [].concat(gaiaWebsiteDatasources);
    _gaiaWebsiteDatasources.splice(index, 1);
    propagateGaiaWebsiteDatasources([].concat(_gaiaWebsiteDatasources));
  }

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && (!gaiaWebsiteDatasources || !gaiaWebsiteDatasources.length)) {
      addGaiaWebsiteDatasourceUrl();
      setInitialized(true);
    }
  }, [gaiaWebsiteDatasources, initialized]);

  return (
    <>
      <Heading className="text-lg">Configure AI chatbot</Heading>
      <Text className="italic text-sm">
        AI will respond in the same language as the input
      </Text>
      <Fieldset>
        {user.customer.isGaiaActivated && (
          <>
            {(gaiaWebsiteDatasources || []).map((field, index) => (
              <Field key={'field-gaia-website-datasource-' + (index + 1)}>
                <Legend>GAIA Website datasource URL</Legend>
                <div className="flex flex-row">
                  <InputGroup className="flex relative items-center">
                    <Input
                      placeholder="Enter a web site datasource url"
                      value={gaiaWebsiteDatasources[index].url ?? ""}
                      onChange={(e) => handleGaiaWebsiteDatasourceUrlChange(e.target.value, index)}
                      showCharacterCount
                      autoComplete="off"
                    />
                  </InputGroup>
                  {gaiaWebsiteDatasources.length > 1 && (
                    <>
                      <Button
                        className="ml-2"
                        color="secondaryButton"
                        onClick={() => deleteGaiaWebsiteDatasourceUrl(index)}>
                        <TrashIcon />
                      </Button>
                    </>
                  )}
                </div>
                {!urlValidator(gaiaWebsiteDatasources[index].url) && (
                  <ErrorMessage>The value is not a valid URL</ErrorMessage>
                )}
              </Field>
            ))}
            {gaiaWebsiteDatasources?.length < 3 && (
              <>
                <Text className="text-sm mb-2">
                  You can add up to 3 urls
                </Text>
                <Button color="secondaryButton" onClick={addGaiaWebsiteDatasourceUrl}>
                  <PlusIcon />
                  Add a website datasource URL
                </Button>
              </>
            )}
          </>
        )}
        < Field >
          <Legend>Assistant context (in English)</Legend>
          <Textarea
            name="Assistant context (in English)"
            key="assistant"
            placeholder="Assistant context"
            value={assistantContext}
            onChange={(e) =>
              handleMessageChange(
                "assistantContext",
                e.target.value,
                nodes,
                setNodes
              )
            }
            maxLength={1024}
            autoComplete="off"
          />
        </Field>
        <Field>
          <Legend>Company context (in English)</Legend>
          <Textarea
            name="Company context (in English)"
            key="company_context"
            placeholder="Company context"
            value={companyContext}
            onChange={(e) =>
              handleMessageChange(
                "companyContext",
                e.target.value,
                nodes,
                setNodes
              )
            }
            maxLength={1024}
            autoComplete="off"
          />
        </Field>
        <Field>
          <Legend>Answer style (in English)</Legend>
          <Textarea
            name="Answer style (in English)"
            key="answer"
            placeholder="Answer style"
            value={answerStyle}
            onChange={(e) =>
              handleMessageChange(
                "answerStyle",
                e.target.value,
                nodes,
                setNodes
              )
            }
            maxLength={1024}
            autoComplete="off"
          />
        </Field>
      </Fieldset >
    </>
  );
};
